import React from "react";

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "1rem 1.125rem",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    position: "relative",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    boxSizing: "border-box",
  },
  pill: (variant) => {
    const map = {
      green: { background: "#F0FDF4", color: "#166534" },
      amber: { background: "#FFFBEB", color: "#92400E" },
      blue: { background: "#EFF6FF", color: "#1E40AF" },
      gray: { background: "#F9FAFB", color: "#4B5563", border: "1px solid #e5e7eb" },
      red: { background: "#FEF2F2", color: "#991B1B" },
    };
    return {
      fontSize: 10,
      fontWeight: 700,
      padding: "2px 9px",
      borderRadius: 20,
      whiteSpace: "nowrap",
      display: "inline-block",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      ...(map[variant] || map.gray),
    };
  },
  name: { fontSize: 16, fontWeight: 700, color: "#1a1d23", marginBottom: 2, paddingRight: 60 },
  email: { fontSize: 12, color: "#6b7280", marginBottom: 1 },
  phone: { fontSize: 12, color: "#6b7280" },
  divider: { borderTop: "1px solid #f3f4f6", borderBottom: "1px solid #f3f4f6", padding: "8px 0", margin: "10px 0" },
  fieldLabel: { fontSize: 11, color: "#9ca3af" },
  fieldValue: { fontSize: 13, fontWeight: 600, color: "#1a1d23" },
  leaseHeading: { fontSize: 12, fontWeight: 600, color: "#1E40AF", marginBottom: 6 },
  noLease: {
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 12,
    color: "#9ca3af",
    textAlign: "center",
    marginBottom: 12,
  },
  btn: {
    marginTop: "auto",
    width: "100%",
    padding: "7px 0",
    borderRadius: 8,
    border: "none",
    background: "#1E40AF",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-TT", {
    style: "currency",
    currency: "TTD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);

const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const statusPillVariant = (status) => {
  switch (status) {
    case "active": return "green";
    case "prospective": return "blue";
    case "past": return "gray";
    case "evicted": return "red";
    default: return "gray";
  }
};

const leasePillVariant = (status) => {
  switch (status) {
    case "Active": return "green";
    case "Expiring Soon": return "amber";
    case "Expired": return "red";
    default: return "gray";
  }
};

// ── Component ──────────────────────────────────────────────────────────────────
function TenantCard({ tenant, onViewDetails }) {
  const status = tenant.status || "prospective";
  const activeLease = tenant.activeLease;
  const fullName = `${tenant.personalInfo.firstName} ${tenant.personalInfo.lastName}`;

  return (
    <div style={s.card}>
      {/* Status pill — top right */}
      <span style={{ ...s.pill(statusPillVariant(status)), position: "absolute", top: 14, right: 14 }}>
        {status}
      </span>

      {/* Header */}
      <div style={{ marginBottom: 10 }}>
        <div style={s.name}>{fullName}</div>
        <div style={s.email}>{tenant.personalInfo.email}</div>
        <div style={s.phone}>{tenant.personalInfo.phone}</div>
      </div>

      {/* Employment strip */}
      <div style={s.divider}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div>
            <div style={s.fieldLabel}>Employer</div>
            <div style={{ ...s.fieldValue, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {tenant.employment?.employer || "N/A"}
            </div>
          </div>
          <div>
            <div style={s.fieldLabel}>Income</div>
            <div style={s.fieldValue}>
              {tenant.employment?.monthlyIncome ? formatCurrency(tenant.employment.monthlyIncome) : "N/A"}
            </div>
          </div>
        </div>
      </div>

      {/* Lease info */}
      {activeLease ? (
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={s.leaseHeading}>Current Lease</div>
            {tenant.currentLeaseStatus && (
              <span style={s.pill(leasePillVariant(tenant.currentLeaseStatus))}>
                {tenant.currentLeaseStatus}
              </span>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 8px" }}>
            <div>
              <div style={s.fieldLabel}>Monthly Rent</div>
              <div style={{ ...s.fieldValue, color: "#1E40AF" }}>{formatCurrency(activeLease.monthlyRent)}</div>
            </div>
            <div>
              <div style={s.fieldLabel}>Lease Start</div>
              <div style={s.fieldValue}>{formatDate(activeLease.leaseStart)}</div>
            </div>
            <div>
              <div style={s.fieldLabel}>Lease End</div>
              <div style={s.fieldValue}>{formatDate(activeLease.leaseEnd)}</div>
            </div>
          </div>
        </div>
      ) : (
        <div style={s.noLease}>No active lease</div>
      )}

      {/* Additional info */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
        <div><strong style={{ color: "#374151" }}>Total Leases:</strong> {tenant.totalLeases || 0}</div>
        <div><strong style={{ color: "#374151" }}>Joined:</strong> {formatDate(tenant.createdAt)}</div>
      </div>

      {/* CTA */}
      <button style={s.btn} onClick={() => onViewDetails(tenant)}>
        View Details
      </button>
    </div>
  );
}

export default TenantCard;
